import { useCallback, useEffect, useState } from "react";

import type { BloodPressureDataPoint } from "~/api/bloodPressure";
import {
  getBloodPressureData,
  getDateRange,
  getGroupByInterval,
} from "~/api/bloodPressure";

interface UseBloodPressureDataProps {
  patientId: number;
  timeRange: string;
  navigationOffset?: number;
  selectedDate?: Date;
}

interface UseBloodPressureDataReturn {
  data: BloodPressureDataPoint[];
  metadata: {
    systolic: number;
    diastolic: number;
  } | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useBloodPressureData = ({
  patientId,
  timeRange,
  navigationOffset = 0,
  selectedDate,
}: UseBloodPressureDataProps): UseBloodPressureDataReturn => {
  const [data, setData] = useState<BloodPressureDataPoint[]>([]);
  const [metadata, setMetadata] = useState<{
    systolic: number;
    diastolic: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const dateRange = getDateRange(timeRange, navigationOffset, selectedDate);
      const groupByInterval = getGroupByInterval(timeRange);


      const response = await getBloodPressureData({
        patient_id: patientId,
        group_by_interval: groupByInterval,
        start_date: dateRange.start_date,
        stop_date: dateRange.stop_date,
      });

      setData(response.data);
      setMetadata(response.metadata);
    } catch (err) {
      console.error("Error fetching blood pressure data:", err);
      setError("Failed to fetch blood pressure data");
      setData([]);
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  }, [patientId, timeRange, navigationOffset, selectedDate]);

  useEffect(() => {
    if (patientId) {
      void fetchData();
    }
  }, [patientId, fetchData]);

  return {
    data,
    metadata,
    loading,
    error,
    refetch: fetchData,
  };
};
